<script lang="ts" setup>
import { onMounted } from 'vue';
import { useDeletionBusinessStore } from './deletionBusinessStore';
import { storeToRefs } from 'pinia';
import RegisterBusinessPageTab from '@/corePagesModule/components/RegisterBusinessPageTab.vue';
import { GetDeletionBusinessAdapter } from '../adapters/getDeletionBusinessAdapter';
import { i18n } from '@/i18n';
import FilterModalButton from '@/corePagesModule/filterModal/FilterModalButton.vue';
import NewBaseTableContent from '@/corePagesModule/BaseTableContent/NewBaseTableContent.vue';
import { TableContentProperty } from '@/corePagesModule/BaseTableContent/interface/tableContentHeaders';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { GetDeletionBusinessUseCase } from '../useCases/getDeletionBusinessUseCase';
import { InventoryRepository } from '@/coreInfrastructureModule/Repositories/InventoryRepository';
import Pagination from '@/corePagesModule/components/Pagination.vue';
import DeleteBusinessModal from './components/modal/DeleteBusinessModal.vue';

const inventoryRepository = new InventoryRepository();
const getDeletionBusinessUseCase = new GetDeletionBusinessUseCase();
const getDeletionBusinessAdapter = new GetDeletionBusinessAdapter();

const store = useDeletionBusinessStore(inventoryRepository, getDeletionBusinessUseCase, getDeletionBusinessAdapter)();
const { state } = storeToRefs(store);

const t = i18n.t;

onMounted(async () => {
  await store.init();
});

/**
 * 取引削除確認モーダルを開く
 */
const openDeleteBusinessModal = () => {
  state.value.deleteBusinessModalState = true;
};
</script>

<template>
  <!-- 取引削除ページ -->
  <div class="deletion-business-table-page">
    <!-- ページタイトル -->
    <div class="deletion-business-table-page__title page-title">
      <span class="page-title__label">
        {{ t('取引削除') }}
      </span>
    </div>
    <!-- ページタブ -->
    <RegisterBusinessPageTab class="deletion-business-table-page__page-tab" />
    <div class="deletion-business-table-page__deletion-business-table deletion-business-table">
      <!-- フィルターとかのアイテム -->
      <div class="deletion-business-table__header-area">
        <div class="deletion-business-table__header-area--control-items">
          <FilterModalButton
            filter-modal-type="deletion-business"
            :select-input-index="0"
            :filter-items="store.getFilterItemsValue().value"
            @apply="(filteredContents) => store.onAppliedFilterModal(filteredContents)"
          />
          <TextButton
            :button-style="'btn-secondary'"
            :size="'btn-md'"
            :on-click="openDeleteBusinessModal"
            :disabled="!state.checkedIds.length"
            class="bulk-action-button-container__export-button"
          >
            {{ t('削除する') }}
          </TextButton>
        </div>
        <div class="deletion-business-table__header-area--pagination-items">
          <Pagination
            v-if="state.pagingOptions"
            :paging-options="state.pagingOptions"
            :is-loading="state.loadingState.status.type === 'loading'"
            class="pagination"
            @on-change-page="store.onChangePaging($event)"
          />
        </div>
      </div>
      <!-- 取引削除テーブル -->
      <div class="deletion-business-table-page__table-area">
        <NewBaseTableContent
          :key="state.renderingKey"
          :prop-table-content-properties="state.tableContentProperties"
          :prop-display-objects="state.tableContentItems"
          :prop-loading-status="state.loadingState"
          :prop-table-type="'deletionBusinessTable'"
          :prop-is-reset="state.isRefreshTable"
          class="unordered-items-table__contains unordered-items-table__contains--table"
          @checked="state.checkedIds = $event"
          @on-click-sort="(sortKey: TableContentProperty, isAsc: boolean) => store.sortTableContentItems(sortKey, isAsc)"
          @reload="store.reloadTable"
        />
      </div>
    </div>
  </div>
  <!-- 取引削除モーダル -->
  <DeleteBusinessModal
    :modal-state="state.deleteBusinessModalState"
    :select-input-index="1"
    @close="state.deleteBusinessModalState = false"
    @remove="store.deleteBusiness"
  />
</template>

<style lang="scss" scoped>
.deletion-business-table-page {
  display: grid;
  gap: 8px;
  margin: 16px;

  &__title {
    font-family: Noto Sans JP;
    font-size: 22px;
    font-weight: 700;
    line-height: 32px;
    letter-spacing: 0em;
    text-align: left;
    color: $textDefault;
  }

  &__table-area {
    overflow: scroll;
  }
}

.deletion-business-table {
  display: grid;
  gap: 8px;
  margin-bottom: 16px;
  &--loading {
    align-self: center;
    height: 100%;
  }

  &__header-area {
    display: grid;
    grid-auto-flow: column;
    gap: 16px;
    align-items: center;
    grid-template-columns: repeat(2, 1fr);

    &--pagination-items {
      display: grid;
      align-items: end;
      .pagination {
        margin-bottom: 0px;
      }
    }

    &--control-items {
      display: grid;
      align-items: center;
      grid-template-columns: repeat(2, fit-content(100%));
      gap: 16px;
    }
  }
}
</style>
